import { apiClient } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type QuoteStatus = 'ACTIVE' | 'APPROVED' | 'CONVERTED' | 'EXPIRED' | 'CANCELLED' | 'REJECTED';
export type StockAvailability = 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK';

export interface QuoteItem {
  id: string;
  product_id: string;
  product_name: string;
  sku: string;
  quantity: number;
  unit_price: number;
  discount: number;
  gst_rate: number;
  line_total: number;
  stock_quantity: number;
  availability: StockAvailability;
  /** Catalogue price now (null offline) — conversion charges this, not unit_price. */
  current_price?: number | null;
}

/** L08 — the person who approved a quote link, as recorded by the API. */
export interface QuoteApproval {
  name: string;
  phone: string | null;
  ip: string | null;
  user_agent: string | null;
  approved_at: string | null;
}

export interface Quote {
  id: string;
  quote_number: string;
  status: QuoteStatus;
  location_id: string;
  customer_id: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  items: QuoteItem[];
  /** GET /quotations list rows carry this summary instead of `items`. */
  item_count?: number;
  subtotal: number;
  tax_total: number;
  discount_total: number;
  grand_total: number;
  valid_until: string;
  notes: string | null;
  created_by: string;
  converted_bill_id: string | null;
  /** Down payments still held against the quote (T10); deducted when it is billed. */
  advance_paid?: number;
  /** grand_total − advance_paid. */
  balance_due?: number;
  /** T11 — the customer's answer on the public quote link. */
  approved_at?: string | null;
  rejected_at?: string | null;
  rejection_reason?: string | null;
  /**
   * L08 — who approved from the link (name + phone they typed). Null for a quote
   * approved before this was recorded. Detail only.
   */
  approval?: QuoteApproval | null;
  /** GET /quotations list rows: the latest change request on the link. */
  last_change_request?: string | null;
  last_change_request_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface QuoteChangeRequest {
  id: string;
  notes: string;
  created_at: string;
}

export type AdvancePaymentMethod = 'CASH' | 'UPI' | 'CARD';
export type AdvanceRefundMode = 'ORIGINAL' | 'STORE_CREDIT';

export interface AddAdvanceDto {
  amount: number;
  payment_method: AdvancePaymentMethod;
  location_id?: string;
}

export interface CancelQuoteResult {
  id: string;
  status: string;
  updated_at: string;
  /** Present when the quote held a down payment. */
  refund?: {
    refund_mode: AdvanceRefundMode;
    amount: number;
    by_method: { payment_method: string; amount: number }[];
    credit_note_code?: string;
  };
}

export interface CreateQuoteDto {
  location_id: string;
  customer_id?: string;
  customer_name?: string;
  customer_phone?: string;
  items: {
    product_id: string;
    quantity: number;
    discount?: number;
  }[];
  validity_days?: number;
  notes?: string;
}

export interface QuoteListFilters {
  status?: string;
  search?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface SendQuoteDto {
  channel: 'WHATSAPP' | 'EMAIL' | 'LINK';
  recipient?: string;
}

// FR-BILL-034 / US-BILL-032: quotation conversion-rate analytics.
// Mirrors the backend response from `GET /quotations/conversion-rate`
// (quotations.service.ts:323-361). Endpoint is MANAGER+ only — callers
// must gate on role before invoking.
export interface ConversionRateFilters {
  location_id?: string;
  start_date?: string;
  end_date?: string;
}

export interface ConversionRateStats {
  total_quotes: number;
  converted: number;
  expired: number;
  active: number;
  conversion_rate_pct: number;
  avg_conversion_time_hours: number;
  total_quoted_value: number;
  total_converted_value: number;
  by_period: Array<{
    date: string;
    quotes: number;
    converted: number;
    rate_pct: number;
  }>;
}

// ─── Service ────────────────────────────────────────────────────────────────

export const quoteService = {
  createQuote(dto: CreateQuoteDto) {
    return apiClient<Quote>('/quotations', {
      method: 'POST',
      body: dto,
    });
  },

  listQuotes(filters: QuoteListFilters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }
    const qs = params.toString();
    return apiClient<{ data: Quote[]; meta: PaginationMeta }>(
      `/quotations${qs ? `?${qs}` : ''}`,
    );
  },

  /**
   * The API returns each line's availability as `stock_availability`, while the
   * UI (and the `QuoteItem` type) reads `availability`. Left unmapped it is
   * always undefined, so `STOCK_INDICATOR[undefined]` blows up when the item
   * rows render and the detail page goes blank. Normalise it here, at the one
   * place the payload enters the app.
   */
  async getQuote(id: string) {
    const quote = await apiClient<Quote>(`/quotations/${id}`);
    return {
      ...quote,
      items: (quote.items ?? []).map((item) => {
        const raw = item as typeof item & { stock_availability?: StockAvailability };
        return { ...item, availability: item.availability ?? raw.stock_availability ?? 'IN_STOCK' };
      }),
    } as Quote;
  },

  updateQuote(id: string, dto: Partial<CreateQuoteDto>) {
    return apiClient<Quote>(`/quotations/${id}`, {
      method: 'PUT',
      body: dto,
    });
  },

  /**
   * Re-quote an expired/cancelled quote as a NEW quote number, priced at the
   * current catalogue. The source quote is left untouched as the record of
   * what was originally offered.
   */
  renewQuote(id: string, validityDays?: number) {
    return apiClient<{
      id: string;
      quote_number: string;
      status: string;
      renewed_from_quotation_id: string | null;
      valid_until: string;
      grand_total: number;
    }>(
      `/quotations/${id}/renew`,
      { method: 'POST', body: validityDays ? { validity_days: validityDays } : {} },
    );
  },

  sendQuote(id: string, dto: SendQuoteDto) {
    return apiClient<{ id: string; channel: string; status: string; approval_link?: string }>(
      `/quotations/${id}/send`,
      { method: 'POST', body: dto },
    );
  },

  /** `refundMode` is required when the quote holds a down payment (T10). */
  cancelQuote(id: string, refundMode?: AdvanceRefundMode) {
    return apiClient<CancelQuoteResult>(`/quotations/${id}/cancel`, {
      method: 'POST',
      body: refundMode ? { refund_mode: refundMode } : {},
    });
  },

  /** Take a down payment against an open quote (T10). */
  addAdvance(id: string, dto: AddAdvanceDto) {
    return apiClient<{ id: string; amount: number; advance_paid: number; balance_due: number }>(
      `/quotations/${id}/advances`,
      { method: 'POST', body: dto },
    );
  },

  /** Change requests the customer sent from the public quote link, newest first. */
  listChangeRequests(id: string) {
    return apiClient<QuoteChangeRequest[]>(`/quotations/${id}/change-requests`);
  },

  getConversionRate(filters: ConversionRateFilters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }
    const qs = params.toString();
    return apiClient<ConversionRateStats>(
      `/quotations/conversion-rate${qs ? `?${qs}` : ''}`,
    );
  },
};
